import CheckIcon from "@mui/icons-material/Check";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { Outfit } from "../../models/Outfit";
import { OutfitPickerItem, PreviewGender } from "./outfitPickerModel";
import OutfitStage from "./OutfitStage";

/** One portrait in the picker grid: the stage, the name and an optional meta line. */
export default function OutfitPickerTile(props: {
  item: OutfitPickerItem;
  gender: PreviewGender;
  ownOutfit?: Outfit;
  /** The value the field currently has. */
  selected: boolean;
  /** The keyboard / pointer highlight, which the details pane shows. */
  highlighted: boolean;
  optionId: string;
  onClick: () => void;
  onDoubleClick: () => void;
}) {
  const { item, gender, ownOutfit, selected, highlighted, optionId, onClick, onDoubleClick } = props;
  const { t } = useTranslation();

  let name: string;
  let meta: string | undefined;
  if (item.kind === "outfit") {
    name = item.outfit.name;
  } else if (item.kind === "default") {
    name = String(t("outfit_picker.default"));
    meta = String(
      ownOutfit
        ? t("outfit_picker.default_meta", { name: ownOutfit.name })
        : t("outfit_picker.default_meta_none")
    );
  } else {
    name = String(t("outfit_picker.random"));
    meta = String(t("outfit_picker.random_meta"));
  }

  return (
    <div
      id={optionId}
      role="option"
      aria-selected={highlighted}
      data-testid={`outfit-tile-${item.key}`}
      className={classNames("flex min-w-0 cursor-pointer flex-col gap-[8px]", {
        "outfitpicker-tile--highlighted": highlighted,
      })}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <OutfitStage item={item} gender={gender} ownOutfit={ownOutfit}>
        {item.kind === "default" && ownOutfit && (
          <span className="absolute bottom-[8px] left-[8px] rounded-full bg-creator-surface px-[8px] py-[2px] text-[11px] font-medium text-creator-text shadow-sm">
            {t("outfit_picker.own_outfit")}
          </span>
        )}
        {selected && (
          <span className="absolute right-[8px] top-[8px] flex h-[22px] w-[22px] items-center justify-center rounded-full bg-creator-primary text-white shadow-md">
            <CheckIcon sx={{ fontSize: 16 }} />
          </span>
        )}
      </OutfitStage>
      <div className="flex min-w-0 flex-col">
        <span
          className={classNames("truncate text-[14px] text-creator-text", selected ? "font-semibold" : "font-medium")}
          title={name}
        >
          {name}
        </span>
        {meta && <span className="truncate text-[12px] text-creator-text-sub">{meta}</span>}
      </div>
    </div>
  );
}

export function OutfitPickerSkeletonTile() {
  return (
    <div className="flex flex-col gap-[8px]" aria-hidden data-testid="outfit-tile-loading">
      <div className="outfitpicker-skeleton" />
      <div className="my-[4px] h-[10px] w-[64%] rounded-full bg-creator-surfaceHover" />
    </div>
  );
}
